<template lang="pug">
.menu-frame.absolute.z-50.start-13.bottom-13.text-xs.w-30(:class="[bgStyle, 'portrait:bottom-19']")
  ul.menu-frame-inner(@click="toggle")
    li.mb-1(v-if="downloadVisible")
      BaseButton.gtm-function.w-full(color="notice", size="sm", data-location="lobby", data-icon="DownloadApp", @click="handleDownload") {{ t('common.download') }}
    li.mb-1(v-if="isShareEnable")
      BaseButton.gtm-function.w-full(color="notice", size="sm", data-location="lobby", data-icon="Share", @click="share") {{ $t('drawer.share') }}
    li.mb-1
      BaseButton.gtm-function.w-full(color="notice", size="sm", data-location="lobby", data-icon="Setting", @click="clickSetting") {{ $t('drawer.setting') }}
    li.mb-1(v-if="isWebView")
      BaseButton.gtm-function.w-full(color="notice", size="sm", data-location="lobby", data-icon="ClearCache", @click="showModal('ModalClearCache')") {{ $t('drawer.clear_cache') }}
    li.mb-1(v-if="!poCurrencyEnable")
      BaseButton.gtm-function.w-full(color="notice", size="sm", data-location="lobby", data-icon="Transaction", @click="clickTransaction") {{ $t('drawer.transaction') }}
    li.mb-1
      BaseButton.gtm-function.w-full(color="notice", size="sm", data-location="lobby", data-icon="History", @click="clickHistory") {{ $t('drawer.history') }}
    li.mb-1
      BaseButton.gtm-function.w-full(color="notice", size="sm", data-location="lobby", data-icon="History", @click="clickFAQ") {{ $t('common.faq') }}
    li(v-if="!isGuest")
      BaseButton.gtm-function.w-full(color="notice", size="sm", data-location="lobby", data-icon="Logout", @click="showModal('ModalLogout')") {{ $t('common.logout') }}
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { useRuntimeConfig } from '#app'
import { useI18n } from '#imports'
import { useMainStore } from '@/stores/main'
import { useSiteConfigStore } from '@/stores/siteConfig'
import { isTrue } from '@/utils/siteConfigChecker'

const emit = defineEmits(['click'])

const config = useRuntimeConfig()
const { $analytics } = useNuxtApp()
const { t } = useI18n()
const { userInfo } = useUserInfo({ autoInit: false })
const { isLoggedIn, isGuest } = useAuthToken()

const { friendActivity } = useActivity('draw-list')
const mainStore = useMainStore()
const { getDownloadLink, downloadVisible, isWebView } = storeToRefs(mainStore)

const { poCurrencyEnable, socialMedia } = useSite()
const { setModal, setModalParams, setDownloadGuide } = mainStore

const siteName = config.public.clientId

const isTestUser = computed(() => userInfo.value?.scope === 'testuser')
const isShareEnable = computed(() => !isTestUser.value && (socialMedia.value.length || friendActivity.value))

const bgStyle = computed(() => {
  if (downloadVisible.value) {
    if (isShareEnable.value) {
      return 'more'
    } else {
      return ''
    }
  } else {
    if (isShareEnable.value) {
      return ''
    } else {
      return 'less'
    }
  }
})

const showModal = (name) => {
  setModal(name)
}

const toggle = () => {
  emit('click')
}

const share = () => {
  $analytics.push({
    event: 'lobby_click',
    location: 'lobby',
    icon: 'Share',
  })
  showModal('ModalShare')
}

const clickSetting = () => {
  $analytics.push({
    event: 'lobby_click',
    location: 'lobby',
    icon: 'Setting',
  })
  showModal('ModalSetting')
}

const clickTransaction = () => {
  $analytics.push({
    event: 'lobby_click',
    location: 'lobby',
    icon: 'transaction',
  })
  if (isGuest.value) {
    setModal('ModalGuestLogin')
  } else {
    setModalParams({ modal: 'transaction' })
    showModal('ModalDepositProxy')
  }
}

const clickHistory = () => {
  $analytics.push({
    event: 'lobby_click',
    location: 'lobby',
    icon: 'History',
  })
  if (isGuest.value) {
    setModal('ModalGuestLogin')
  } else {
    showModal('ModalStatement')
  }
}

const clickFAQ = () => {
  showModal('ModalFAQ')
}

const handleDownload = () => {
  $analytics.push({
    event: 'lobby_click',
    location: 'lobby',
    icon: 'DownloadApp',
  })
  setDownloadGuide(true)

  // 創建臨時連結並點擊，避免被瀏覽器阻擋
  const link = document.createElement('a')
  link.href = getDownloadLink.value
  link.target = '_blank'
  link.rel = 'nofollow noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";

.drawer ul {
  background-image:
    url('assets/images/drawer/index_img_dialogframe_normal_left.png'),
    url('assets/images/drawer/index_img_dialogframe_normal_middle.png'),
    url('assets/images/drawer/index_img_dialogframe_normal_right.png');
  background-repeat: no-repeat, no-repeat, no-repeat;
  background-position: left, left 1.55rem top 0, right;
  background-size: contain, calc(100% - 3.1rem) 100%, contain;
  @apply flex flex-col justify-between w-30 px-3 py-2.5 text-xs;
}

.drawer ul.more {
  background-position: left, left 1.8rem top 0, right;
  background-size: contain, calc(100% - 3.6rem) 100%, contain;
}

.drawer ul.less {
  background-position: left, left 1.3rem top 0, right;
  background-size: contain, calc(100% - 2.5rem) 100%, contain;
}

.drawer ul.onlyOne {
  background-position: left, left 1rem top 0, right;
  background-size: contain, calc(100% - 1.8rem) 100%, contain;
}

.menu-frame {
  @apply rounded-2xl border-menu-field-border bg-gradient-to-b from-menu-field-from to-menu-field-to p-1;
  border-width: 0.2rem;
  border-style: solid;
}

.menu-frame::before {
  content: '';
  position: absolute;
  border: 0.0625rem solid #ffffffcc;
  inset: -0.125rem;
  border-radius: 0.9rem;
  pointer-events: none;
}

@media (display-mode: standalone) and (orientation: portrait) {
  .menu-frame {
    @apply bottom-8;
  }
}

.menu-frame-inner {
  @apply relative bg-gradient-to-b from-menu-bg-from to-menu-bg-to rounded-xl border-menu-bg-border text-menu-bg-border p-1;
  border-width: 0.2rem;
  border-style: solid;
}

.menu-frame-inner::before {
  content: '';
  position: absolute;
  border: 0.0625rem solid #ffffff7f;
  inset: -0.125rem;
  border-radius: 0.7rem;
  pointer-events: none;
}

.menu-frame-inner li {
  @apply z-0;
}
</style>
